/**
 * InstallCode - the ONE place all website snippets live, organized in tabs:
 *   Site tracking | WhatsApp click | Form embed
 *
 * Single source of truth for every snippet string (so the tracking key, endpoint
 * and tracker tag are always consistent - the key is baked into the tracker tag
 * here, everywhere it appears). Rendered from both Settings → Tracking and the
 * form Embed view, so tenants never hunt across pages for code.
 *
 * Fetches the workspace's ingest key from /api/settings/tracking (authed → the
 * right tenant). Accepts the org's forms for the Form-embed tab's picker.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { snippetBase } from '@/lib/urls';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { cn, fmtZoned } from '@/lib/utils';
import { MoreInfo } from '@/components/ui/calm';
import { Link } from 'react-router-dom';
import { useTrackingDomains } from '@/hooks/use-tracking-domains';
import { scriptOrigin, pathOnlyOrigin, TRACKING_DOMAIN_SETUP } from '@/lib/script-origin';

// ─── snippet builders (single source of truth) ──────────────────────────────
/**
 * @param {object|null} origin result of scriptOrigin(), or null for our domain.
 *
 * The URL here is not cosmetic. Wherever this script is fetched from is where
 * it posts, because the server bakes the endpoint in at serve time. A snippet
 * pointing at our domain keeps every later request third-party no matter what
 * the workspace has configured, which is exactly what used to happen: this tag
 * was hardcoded to our host while the commerce pixel below it honoured the
 * tenant's transport.
 */
export function siteTrackingTag(base, key, origin = null) {
  const k = key || 'YOUR_KEY';
  // First-party hosts serve the beacon at /mm.js; our own domain serves the
  // same file at the historical /form/mm-tracking.js so tags already pasted
  // into a live <head> keep working untouched.
  const src = origin?.base
    ? `${origin.base}/mm.js?key=${k}`
    : `${base}/form/mm-tracking.js?key=${k}`;
  // Key is in BOTH the URL (?key=) and data-mm-key. The URL key is the robust
  // one: GTM Custom HTML can strip data-* attributes, but never the src query
  // string. The script reads data-mm-key first, then falls back to ?key=.
  return `<!-- Cookiebees attribution + page journey - add on EVERY page (GTM: Custom HTML tag, trigger All Pages) -->
<script src="${src}" data-mm-key="${k}" async></script>`;
}

// ─── commerce pixel ─────────────────────────────────────────────────────────
// Where each snippet loads from: lib/script-origin (pure, so it is tested
// without the UI), re-exported here where every snippet surface imports it.
export { scriptOrigin, pathOnlyOrigin, TRACKING_DOMAIN_SETUP };

// Every option px.js takes, in ONE place, because nobody should have to recall
// that the cart-attribute flag is spelled `cart_attr` eight months from now.
// The UI renders these as checkboxes (CommercePixelBuilder below) and builds the
// URL from them, so the flag names live in code rather than in someone's memory.
//
// Off by default, except the two marked `recommended` on a Shopify store (the
// visitor ID and the campaign data on the cart): they are how an order keeps
// its attribution through a checkout app or a blocked pixel, so a Shopify
// snippet carries them unless someone switches them off. A snippet already
// pasted keeps the flags it was copied with. `shopifyOnly` options are not
// offered to other stores.
export const PIXEL_OPTIONS = [
  {
    flag: 'auto',
    label: 'Auto-capture dataLayer events',
    summary: 'Your store\'s GA4 shop events. Off if GTM or a custom pixel sends them.',
    help: 'Forwards standard GA4 ecommerce pushes (view_item, add_to_cart, begin_checkout…) with no tags to build. Leave OFF if you already send events yourself from GTM or a Shopify custom pixel, or each one arrives twice.',
  },
  {
    flag: 'purchase',
    label: 'Capture GA4 purchase from dataLayer',
    summary: 'Also forwards a GA4 purchase push as an order.',
    help: 'Also forwards a GA4 `purchase` push to the order pipeline, so a store that already fires one needs no extra tag. Needs the option above.',
    requires: 'auto',
  },
  {
    flag: 'cart_attr',
    label: 'Write the visitor ID to the cart',
    summary: 'Keeps the visitor on the order through a blocked pixel or checkout app.',
    shopifyOnly: true,
    recommended: true,
    help: 'Adds mm_cid to the cart so it lands on the order itself, which survives a blocked pixel or a checkout on the provider\'s own domain. Merges with other apps\' attributes, never replaces them. Shopify only.',
  },
  {
    flag: 'recs',
    label: 'Show recommended products in the cart',
    summary: 'Shows nothing until switched on in Commerce > Recommendations.',
    help: 'Renders a "goes with your cart" widget on the cart page and cart drawer, picked from your own orders and each visitor\'s history - with proof lines (buyer counts, ratings) from real data. A holdout slice measures true lift. Switch it on and tune title/products/holdout in Commerce > Recommendations; the flag alone renders nothing until that switch is on.',
  },
  {
    flag: 'cart_ctx',
    label: 'Write the campaign data to the cart',
    summary: 'Every UTM and click ID on the order, visible in Shopify.',
    shopifyOnly: true,
    recommended: true,
    help: 'Adds every UTM, click ID, pixel cookie and first-touch we captured as one mm_tracking attribute on the order - the backup that saves attribution when the cookie is gone and the store keeps no campaign data of its own. Click IDs are written first and anything past Shopify\'s size limit is dropped whole, never truncated. Note it is visible on the order in your Shopify admin.',
    requires: 'cart_attr',
  },
];

/**
 * Which transport this snippet uses, said plainly under the snippet itself.
 *
 * Written because the tenant cannot tell by looking. The host in the src is the
 * only visible difference between a request ad blockers ignore and one they
 * drop, and the consequences (cookie lifetime, Safari, blocker lists) are not
 * guessable from a URL.
 *
 * THE ONE PLACE a snippet says it is temporary. Every surface that hands out a
 * tag (install code, store connect, the WhatsApp widget, chat, popups, the
 * recommendations tag, the install matrix) renders this under it, so a
 * workspace without a working tracking domain is told on every one of them
 * that the tag loads from Cookiebees for now and switches itself once the
 * domain works. Nothing is refused and nothing stops collecting: the snippet is
 * always shown, and a tag on our domain keeps sending.
 */
export function TransportNote({ fp }) {
  const tone = fp?.kind === 'path' ? 'emerald' : fp?.kind === 'subdomain' ? 'amber' : 'temp';
  const setup = (
    <Link to={TRACKING_DOMAIN_SETUP} className="font-medium text-primary underline-offset-2 hover:underline">
      Finish your tracking domain
    </Link>
  );
  const why = fp?.why || {};
  const stopped = why.state === 'stopped' && why.stopped?.length
    ? why.stopped[0] : null;
  const body = fp?.kind === 'path'
    ? <>Loading from <b>{fp.label}</b>, a path on your own domain: the strongest setup.{' '}
      <MoreInfo label="Why a path is strongest">Nothing in DNS for a blocker to detect and no CNAME for Safari to call cloaking, so your visitor ID is re-issued server-side for 400 days instead of the 7 Safari allows a script.</MoreInfo></>
    : fp?.kind === 'subdomain'
      ? <>Loading from <b>{fp.label}</b>, your own subdomain.{' '}
        <MoreInfo label="Subdomain or path">Better than our domain, but Safari treats a CNAME like this as third-party and caps the cookie at 7 days. A path on your main domain avoids that.</MoreInfo></>
      : why.subdomainOnly
        ? <><b>Temporary:</b> this loads from Cookiebees, because a tracking subdomain does not carry this script. A full first-party path does: {setup} with a path and this snippet switches to your own domain.</>
        : stopped
          ? <><b>Temporary:</b> this loads from Cookiebees because your tracking domain{stopped.host ? <> on <b>{stopped.host}</b></> : null} stopped carrying traffic{stopped.last_seen_at ? ` on ${fmtZoned(stopped.last_seen_at, 'd MMM')}` : ''}. {setup} and this snippet switches back to your own domain on its own.</>
          : <><b>Temporary:</b> loading from Cookiebees, so blockers can drop it and Safari caps visitor IDs at 7 days. {setup} to move it to your domain.</>;
  return (
    <p className={cn('text-[13px] leading-relaxed',
      tone === 'emerald' ? 'text-emerald-700 dark:text-emerald-400'
        : tone === 'amber' ? 'text-amber-700 dark:text-amber-400'
          : 'rounded-md border border-amber-500/30 bg-amber-500/[0.07] px-3 py-2 text-amber-900 dark:text-amber-200')}>
      {body}
    </p>
  );
}

/**
 * The image pixel (api services/imagePixel): a page view from a browser that
 * runs no scripts. Inside <noscript>, so it loads only when the script tag did
 * not run and can never count a page twice. First-party only: on our domain its
 * cookie would be third-party, so there is no snippet for that transport.
 * no-referrer-when-downgrade so a tracking SUBDOMAIN (cross-origin) still gets
 * the page's full URL, click ids included, rather than its origin alone.
 */
export function imagePixelSnippet(firstPartyBase) {
  if (!firstPartyBase) return null;
  return `<noscript><img src="${firstPartyBase}/i.gif" width="1" height="1" alt="" referrerpolicy="no-referrer-when-downgrade" style="display:none"></noscript>`;
}

export function commercePixelTag(base, key, opts = {}, origin = null) {
  const k = key || 'YOUR_KEY';
  const on = PIXEL_OPTIONS
    .filter((o) => opts[o.flag] && (!o.requires || opts[o.requires]))
    .map((o) => `&${o.flag}=1`)
    .join('');
  // A verified first-party domain serves the beacon from its own root, and the
  // script it hands back posts to that same host - which is the whole reason to
  // have one. Falling back to our domain here would quietly undo it.
  // A path origin already carries its prefix; a subdomain serves at its root.
  const src = origin?.base
    ? `${origin.base}/px.js?key=${k}${on}`
    : `${base}/api/commerce/px.js?key=${k}${on}`;
  return `<script async src="${src}"></script>`;
}

// The WhatsApp website widget. One line, key only: the number, agents, greeting
// and every other setting are saved in Settings > WhatsApp > Website widget and
// read by the widget at load (api/src/routes/waWidget.js). The snippet used to
// carry &phone=&msg=, which froze whatever was typed at copy time into the
// tenant's theme - measuremarketer.com shipped phone=9199 that way. Old
// snippets still work; the widget uses their params only when nothing is saved.
//
// On a tracking subdomain or path the widget is served from the tenant's own
// origin, which bakes in first-party addresses for its click tickets and cart
// events (api/src/services/waWidgetScript.js). Same file name on every
// transport: the widget finds its own tag by it.
export function whatsappWidgetSnippet(base, key, origin = null) {
  const k = key || 'YOUR_KEY';
  const src = origin?.base
    ? `${origin.base}/wa-widget.js?key=${encodeURIComponent(k)}`
    : `${base}/form/wa-widget.js?key=${encodeURIComponent(k)}`;
  return `<!-- Cookiebees WhatsApp widget: every page, before </body> (or a GTM Custom HTML tag on All Pages) -->
<script src="${src}" async></script>`;
}

export function whatsappClickSnippet() {
  return `<script>
  // Fire this when your WhatsApp button is clicked (GTM Custom HTML tag, or onclick):
  var id = window.mmTracking ? window.mmTracking.track() : '';   // sends the visitor's data to us, returns the stable id
  var phone = '9199xxxxxxx';
  location.href = 'https://wa.me/' + phone + '?text=' + encodeURIComponent(id + ' Hi, I would like to know more');
</script>`;
}

export function whatsappStandalone(base, key, origin = null) {
  return `<script>
(function () {
  var KEY = '${key || 'YOUR_KEY'}';
  var ck = function (n) { var p = ('; ' + document.cookie).split('; ' + n + '='); return p.length === 2 ? decodeURIComponent(p.pop().split(';').shift()) : ''; };
  var id   = ck('mm_id') || (function () { try { return localStorage.getItem('mm_id') || ''; } catch (e) { return ''; } })();
  var blob = (function () { try { return JSON.parse(ck('mm_tracking') || localStorage.getItem('mm_tracking') || '{}'); } catch (e) { return {}; } })();
  var body = JSON.stringify({ id: id, cookies: blob });
  var url  = '${origin?.base ? `${origin.base}/t` : `${base}/api/track`}/' + KEY;
  try { if (!navigator.sendBeacon(url, new Blob([body], { type: 'text/plain' }))) throw 0; }
  catch (e) { fetch(url, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: body, keepalive: true }); }
})();
</script>`;
}

export function iframeEmbed(url, formOrigin) {
  return `<!-- Cookiebees booking form -->
<iframe
  id="mm-crm-form"
  src="${url}"
  width="100%"
  height="900"
  frameborder="0"
  style="border:0; max-width:680px; display:block; margin:0 auto;"
  title="Book your strategy call">
</iframe>
<script>
(function () {
  var FORM_ORIGIN = '${formOrigin}';
  var iframe = document.getElementById('mm-crm-form');
  function readTracking() {
    try { return JSON.parse(localStorage.getItem('mm_tracking') || '{}'); }
    catch (_) { return {}; }
  }
  window.dataLayer = window.dataLayer || [];
  window.addEventListener('message', function (e) {
    if (e.origin !== FORM_ORIGIN) return;
    if (!e.data || e.data.source !== 'mm-crm-form') return;
    // The Cookiebees tag on this page already pushes these (its form bridge).
    if (window.__cbFormBridge === 'tag') return;
    if (e.data.type === 'mm:form:view') {
      try {
        iframe.contentWindow.postMessage({
          source: 'mm-crm-parent',
          type: 'mm:parent-context',
          payload: { tracking: readTracking(), search: window.location.search, href: window.location.href, referrer: document.referrer }
        }, FORM_ORIGIN);
      } catch (_) {}
    }
    window.dataLayer.push({ event: e.data.type, mm_payload: e.data.payload, mm_ts: e.data.ts });
  });
})();
</script>`;
}

// Two popup variants from one core, because the audiences are different
// people: "Popup with our button" is for a page owner who wants something
// working in one paste; "Popup from your own buttons" is for designed sites
// (Framer, Webflow, custom themes) whose CTAs must not change by a pixel.
// One snippet explaining both modes in comments made every recipient read
// about the mode they were not using.
function modalCore(url, formOrigin, triggers, triggerComment) {
  return `<script>
(function () {
  var FORM_ORIGIN = '${formOrigin}';
  var FORM_SRC = '${url}';
  ${triggerComment}
  var TRIGGERS = '${triggers}';
  var overlay = null;
  function readTracking() {
    try { return JSON.parse(localStorage.getItem('mm_tracking') || '{}'); }
    catch (_) { return {}; }
  }
  window.dataLayer = window.dataLayer || [];
  function build() {
    overlay = document.createElement('div');
    overlay.setAttribute('role', 'dialog');
    overlay.style.cssText = 'position:fixed;inset:0;z-index:99999;display:flex;align-items:flex-start;justify-content:center;background:rgba(0,0,0,.6);padding:24px;overflow:auto;';
    var box = document.createElement('div');
    box.style.cssText = 'position:relative;width:100%;max-width:680px;margin:auto;background:#fff;border-radius:12px;overflow:hidden;box-shadow:0 20px 60px rgba(0,0,0,.35);';
    var close = document.createElement('button');
    close.type = 'button'; close.innerHTML = '&times;'; close.setAttribute('aria-label', 'Close');
    close.style.cssText = 'position:absolute;top:6px;right:12px;z-index:1;border:0;background:transparent;font-size:30px;line-height:1;color:#888;cursor:pointer;';
    close.onclick = hide;
    var iframe = document.createElement('iframe');
    iframe.id = 'mm-crm-form'; iframe.src = FORM_SRC; iframe.title = 'Book your strategy call';
    iframe.setAttribute('frameborder', '0');
    iframe.style.cssText = 'border:0;width:100%;height:900px;max-height:86vh;display:block;';
    box.appendChild(close); box.appendChild(iframe); overlay.appendChild(box);
    overlay.addEventListener('click', function (e) { if (e.target === overlay) hide(); });
    document.body.appendChild(overlay);
  }
  function show() { if (!overlay) build(); overlay.style.display = 'flex'; document.body.style.overflow = 'hidden'; }
  function hide() { if (overlay) overlay.style.display = 'none'; document.body.style.overflow = ''; }
  document.addEventListener('click', function (e) {
    var t = e.target && e.target.closest ? e.target.closest(TRIGGERS) : null;
    if (!t) return;
    e.preventDefault();
    show();
  }, true);
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape') hide(); });
  window.addEventListener('message', function (e) {
    if (e.origin !== FORM_ORIGIN) return;
    if (!e.data || e.data.source !== 'mm-crm-form') return;
    // The Cookiebees tag on this page already pushes these (its form bridge).
    if (window.__cbFormBridge === 'tag') return;
    if (e.data.type === 'mm:form:view') {
      try {
        var f = document.getElementById('mm-crm-form');
        f && f.contentWindow.postMessage({
          source: 'mm-crm-parent',
          type: 'mm:parent-context',
          payload: { tracking: readTracking(), search: window.location.search, href: window.location.href, referrer: document.referrer }
        }, FORM_ORIGIN);
      } catch (_) {}
    }
    window.dataLayer.push({ event: e.data.type, mm_payload: e.data.payload, mm_ts: e.data.ts });
  });
})();
</script>`;
}

// Popup opened by the site's OWN buttons - injects nothing visual.
export function modalEmbedOwnButtons(url, formOrigin) {
  return `<!-- Cookiebees booking popup - triggered by YOUR OWN buttons.
     Add data-mm-book to any button or link and the popup opens on click:
       <a href="#" data-mm-book>Book your event</a>
     Your design is untouched. To match existing buttons without editing them,
     widen TRIGGERS below, e.g. '[data-mm-book], .book-your-event'.
     Place this before </body>. -->
${modalCore(url, formOrigin, '[data-mm-book]',
    `// Any element matching this opens the popup. Delegated, so buttons
  // rendered later (carousels, SPAs) work too.`)}`;
}

// Popup with a ready-made button - one paste and it works.
export function modalEmbed(url, formOrigin, buttonLabel = 'Book your strategy call') {
  return `<!-- Cookiebees booking form - button + popup, works as pasted. -->
<button type="button" id="mm-open-form"
  style="display:inline-block;padding:14px 28px;background:#16865E;color:#fff;border:0;border-radius:8px;font-size:16px;font-weight:600;cursor:pointer;">
  ${buttonLabel}
</button>
${modalCore(url, formOrigin, '#mm-open-form', '// The button above opens the popup.')}`;
}

// ─── UI ──────────────────────────────────────────────────────────────────────
export function CopyBlock({ code }) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  return (
    // Button above the block, not floating over it. A snippet is one long
    // scrolling line; a button pinned to the container does not scroll with
    // the code, so everything past the first screenful passes underneath it.
    <div>
      <div className="mb-1 flex justify-end">
        <Button
          size="sm" variant="outline" className="h-6 px-2 text-[13px]"
          onClick={() => { try { navigator.clipboard.writeText(code); } catch (_) {} setCopied(true); setTimeout(() => setCopied(false), 1500); toast({ title: 'Copied' }); }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <pre className="overflow-x-auto rounded-md border bg-muted/40 p-3 text-[13px] leading-relaxed"><code>{code}</code></pre>
    </div>
  );
}

/**
 * The site script's options, as switches in two groups: what a Shopify store
 * should run (on by default) and what is optional. Each row is a label and one
 * line; the full reasoning folds out under "Details", so the list can be read
 * at a glance. Turning off a parent turns off what depends on it, so a copied
 * snippet never claims an option px.js would ignore.
 */
export function defaultPixelOpts(platform) {
  const o = {};
  if (platform === 'shopify') for (const x of PIXEL_OPTIONS) if (x.recommended) o[x.flag] = true;
  return o;
}

export function PixelOptions({ opts, setOpts, platform }) {
  const [open, setOpen] = useState(null);
  const list = PIXEL_OPTIONS.filter((o) => platform === 'shopify' || !o.shopifyOnly);
  const toggle = (flag) => setOpts((o) => {
    const next = { ...o, [flag]: !o[flag] };
    for (const o2 of PIXEL_OPTIONS) if (o2.requires && !next[o2.requires]) next[o2.flag] = false;
    return next;
  });
  const groups = platform === 'shopify'
    ? [['Recommended for Shopify', list.filter((o) => o.recommended)], ['Optional', list.filter((o) => !o.recommended)]]
    : [['Options', list]];
  return (
    <div className="overflow-hidden rounded-xl border border-border">
      {groups.map(([title, items], gi) => (
        <div key={title} className={cn(gi > 0 && 'border-t border-border')}>
          <p className="bg-muted/40 px-4 py-2 text-[13px] font-semibold text-muted-foreground">{title}</p>
          {items.map((o) => {
            const blocked = o.requires && !opts[o.requires];
            const needs = o.requires && PIXEL_OPTIONS.find((x) => x.flag === o.requires);
            return (
              <div key={o.flag} className={cn('border-t border-border/60 px-4 py-3 first:border-t-0', blocked && 'opacity-55')}>
                <div className="flex items-start gap-3">
                  <Switch checked={!!opts[o.flag]} disabled={blocked} onCheckedChange={() => !blocked && toggle(o.flag)} className="mt-0.5" aria-label={o.label} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                      <span className="text-[14.5px] font-medium">{o.label}</span>
                      <button type="button" onClick={() => setOpen(open === o.flag ? null : o.flag)}
                        className="text-[13px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline">
                        {open === o.flag ? 'Hide details' : 'Details'}
                      </button>
                    </div>
                    <p className="text-[13.5px] leading-snug text-muted-foreground">
                      {o.summary || o.help}{blocked && needs ? ` Needs "${needs.label}".` : ''}
                    </p>
                    {open === o.flag && <p className="mt-1.5 rounded-md bg-muted/40 px-3 py-2 text-[13.5px] leading-relaxed text-muted-foreground">{o.help}</p>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/**
 * ImagePixelBlock - the second paste, shown right under every site tag (owner,
 * 10 Oct 2026: "in the same place where we give the px.js code"). The script
 * goes in <head>; this goes immediately after <body>. On our own domain it
 * cannot work (its cookie would be third-party), so it says what unlocks it
 * instead of handing out a snippet that would do nothing.
 */
export function ImagePixelBlock({ fp, file = 'your theme' }) {
  const code = imagePixelSnippet(fp?.kind !== 'ours' ? fp?.base : null);
  return (
    <div className="space-y-2 rounded-lg border border-border bg-muted/20 p-3.5">
      <p className="text-[14px] font-medium">And right after <span className="font-mono text-[13px]">&lt;body&gt;</span>: the image pixel</p>
      {code ? (
        <>
          <p className="text-[13.5px] leading-relaxed text-muted-foreground">
            In the same file ({file}), right after the opening <span className="font-mono text-[12.5px]">&lt;body&gt;</span> tag.{' '}
            <MoreInfo label="What the image pixel does">It counts visitors whose browser runs no scripts (script blockers, NoScript, locked-down work browsers). It loads only when the
            script above could not run, so no page is counted twice.</MoreInfo>
          </p>
          <CopyBlock code={code} />
        </>
      ) : (
        <p className="text-[13.5px] leading-relaxed text-muted-foreground">
          Needs your own address: the image pixel keeps the visitor with a cookie set from your site, which our domain cannot do.{' '}
          <Link to={TRACKING_DOMAIN_SETUP} className="font-medium text-primary underline-offset-2 hover:underline">Set up a path or subdomain</Link>, and the snippet appears here.
        </p>
      )}
    </div>
  );
}

/**
 * CommercePixelBuilder - the tag for a custom-built store, with its options.
 * Shopify-only options are not offered here.
 */
export function CommercePixelBuilder({ base, tkey }) {
  const [opts, setOpts] = useState({});
  const { data: td } = useTrackingDomains();
  const fp = scriptOrigin(td);
  return (
    <div className="space-y-3">
      <CopyBlock code={commercePixelTag(base, tkey, opts, fp)} />
      <TransportNote fp={fp} />
      <ImagePixelBlock fp={fp} file="your site template" />
      <PixelOptions opts={opts} setOpts={setOpts} platform="custom" />
    </div>
  );
}

// The webhook URL itself, shown plainly (not buried in a snippet) with a copy
// button - this is what a tenant POSTs to / tests from Postman.
export function EndpointBox({ url }) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  return (
    <div className="rounded-md border card-good p-3">
      <div className="text-[13px] font-medium first-letter:uppercase text-emerald-700 dark:text-emerald-400">Your tracking endpoint - POST here</div>
      <div className="mt-1.5 flex items-center gap-2">
        <code className="block flex-1 select-all break-all rounded bg-background/70 px-2 py-1.5 font-mono text-xs text-foreground">{url || 'loading…'}</code>
        <Button
          size="sm" variant="outline" className="h-8 flex-none"
          onClick={() => { try { navigator.clipboard.writeText(url); } catch (_) {} setCopied(true); setTimeout(() => setCopied(false), 1500); toast({ title: 'Endpoint copied' }); }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <p className="mt-1.5 text-[13px] text-emerald-700/90 dark:text-emerald-400/90">
        Send <code className="rounded bg-background/60 px-1">POST</code> JSON <code className="rounded bg-background/60 px-1">{'{ id, cookies }'}</code> here (no auth header - the key in the URL identifies your workspace). This is the webhook behind the WhatsApp click-stitch; you can test it straight from Postman.
      </p>
    </div>
  );
}

export function InstallCode({ forms = [], defaultFormId }) {
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    api.get('/api/settings/tracking').then(({ data }) => setCfg(data)).catch(() => {});
  }, []);
  const base = snippetBase(cfg);
  const key = cfg?.ingest_key || '';
  // The workspace's transport, resolved once and applied to EVERY snippet
  // below. It used to be read only by the commerce pixel, so a workspace with
  // a working first-party domain was still handed a lead-beacon tag and a
  // WhatsApp tag pointing at our host - third-party requests it had already
  // done the work to avoid.
  const { data: td } = useTrackingDomains();
  const fp = scriptOrigin(td);
  const endpoint = key ? `${fp.base ? `${fp.base}/t` : `${base}/api/track`}/${key}` : '';

  const [formId, setFormId] = useState(defaultFormId || (forms[0] && forms[0].id) || '');
  useEffect(() => { if (defaultFormId) setFormId(defaultFormId); }, [defaultFormId]);
  // `forms` arrives async on the Tracking tab, so formId may have initialized to
  // '' before the list loaded. Once forms exist, adopt the first one if the
  // current id doesn't match any - otherwise the snippet renders empty.
  useEffect(() => {
    if (forms.length && !forms.some((f) => String(f.id) === String(formId))) {
      setFormId(forms[0].id);
    }
  }, [forms, formId]);
  // Fallback to the first form so a single-form workspace (picker hidden) or the
  // brief pre-sync render still produces a snippet.
  const form = useMemo(
    () => forms.find((f) => String(f.id) === String(formId)) || forms[0] || null,
    [forms, formId]
  );
  const [mode, setMode] = useState('inline');

  const formUrl = useMemo(() => {
    if (!form) return '';
    let u = `${base}/form/?formId=${form.id}`;
    if (!form.assigned_user_id) {
      const uid = (typeof window !== 'undefined' && localStorage.getItem('userId')) || '';
      if (uid) u += `&userId=${uid}`;
    }
    return u;
  }, [form, base]);
  const formEmbed = form
    ? (mode === 'modal' ? modalEmbed(formUrl, base)
      : mode === 'modal-own' ? modalEmbedOwnButtons(formUrl, base)
        : iframeEmbed(formUrl, base))
    : '';

  return (
    <Tabs defaultValue="site">
      <TabsList>
        <TabsTrigger value="site">Site tracking</TabsTrigger>
        <TabsTrigger value="whatsapp">WhatsApp click</TabsTrigger>
        <TabsTrigger value="form">Form embed</TabsTrigger>
      </TabsList>

      {/* Site tracking */}
      <TabsContent value="site" className="space-y-3 pt-3">
        <div className="rounded-md border card-attention p-3">
          <div className="text-[13px] font-medium first-letter:uppercase text-amber-700 dark:text-amber-400">Your tracking key</div>
          <code className="mt-1 block select-all break-all font-mono text-xs text-foreground">{key || 'loading…'}</code>
          <p className="mt-1.5 text-[13px] text-amber-700/90 dark:text-amber-400/90">
            Baked into your live pages. Don&apos;t change or remove it, or attribution + WhatsApp stitching break. It&apos;s protected from accidental edits.
          </p>
        </div>
        <EndpointBox url={endpoint} />
        <p className="text-[13px] text-muted-foreground">
          Add to the <code className="rounded bg-muted px-1">&lt;head&gt;</code> of <b>every page</b>. Captures UTMs and click IDs / pixel cookies for
          <b> Meta, Google (Ads + GA4), LinkedIn, Microsoft (Bing), TikTok, Snapchat, Pinterest, Reddit, ChatGPT Ads &amp; X</b> plus first-touch, mints the stable
          <code className="rounded bg-muted px-1">mm_id</code>, and stores it all in the <code className="rounded bg-muted px-1">mm_id</code> + <code className="rounded bg-muted px-1">mm_tracking</code> cookies. Posts nothing by itself.
        </p>
        <CopyBlock code={siteTrackingTag(base, key, fp)} />
        <TransportNote fp={fp} />
        <ImagePixelBlock fp={fp} file="your site template" />
      </TabsContent>

      {/* WhatsApp click */}
      <TabsContent value="whatsapp" className="space-y-3 pt-3">
        <EndpointBox url={endpoint} />
        <p className="text-[13px] text-muted-foreground">
          Fire on a WhatsApp-button click (GTM <b>Custom HTML</b> tag on a WhatsApp-link click trigger, or an
          <code className="mx-1 rounded bg-muted px-1">onclick</code>). One line: <code className="rounded bg-muted px-1">track()</code> sends
          the visitor&apos;s data and returns the stable id for your message. The posting code stays inside our script, so you don&apos;t maintain it.
        </p>
        <CopyBlock code={whatsappClickSnippet()} />
        <details className="rounded-md border bg-muted/20 p-3">
          <summary className="cursor-pointer text-xs font-semibold">Advanced: self-contained tag (no dependency on our script)</summary>
          <p className="mt-2 text-[13px] text-muted-foreground">Only if a page can&apos;t load the site-tracking script. You maintain this copy:</p>
          <div className="mt-2"><CopyBlock code={whatsappStandalone(base, key, fp)} /></div>
        </details>
        <TransportNote fp={fp} />
      </TabsContent>

      {/* Form embed */}
      <TabsContent value="form" className="space-y-3 pt-3">
        {forms.length === 0 ? (
          <p className="text-[13px] text-muted-foreground">Create a form first (Forms), then its embed code appears here.</p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              {forms.length > 1 && (
                <Select value={String(formId)} onValueChange={(v) => setFormId(v)}>
                  <SelectTrigger className="h-8 w-[260px]"><SelectValue placeholder="Pick a form" /></SelectTrigger>
                  <SelectContent>
                    {forms.map((f) => <SelectItem key={f.id} value={String(f.id)}>{f.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <div className="ml-auto flex items-center gap-1.5">
                {[{ k: 'inline', l: 'Inline form' }, { k: 'modal', l: 'Popup + our button' }, { k: 'modal-own', l: 'Popup + your buttons' }].map((m) => (
                  <button key={m.k} type="button" onClick={() => setMode(m.k)}
                    className={cn('rounded-full border px-2.5 py-1 text-[13px] transition-colors',
                      mode === m.k ? 'border-primary/25 bg-primary-soft/60 text-primary' : 'border-border bg-muted/40 text-muted-foreground hover:bg-muted')}>
                    {m.l}
                  </button>
                ))}
              </div>
            </div>
            <p className="text-[13px] text-muted-foreground">
              {mode === 'modal'
                ? 'Drops a ready-made CTA button; clicking it opens the form in a centered popup. Same tracking + GTM dataLayer events as inline.'
                : mode === 'modal-own'
                  ? 'Injects nothing visual - the site keeps its own designed buttons. Add data-mm-book to any button or link (in Framer/Webflow: set the layer link to # and widen TRIGGERS, or add the attribute via an embed/override) and clicking it opens the popup. Paste before </body>.'
                  : 'Add to the page where the form should appear. Reads the site-tracking data, forwards it to the form, and pushes form events to window.dataLayer for GTM.'}
              {' '}Submissions land in Leads with full attribution.
            </p>
            <CopyBlock code={formEmbed} />
          </>
        )}
      </TabsContent>
    </Tabs>
  );
}
